import Modal from "react-modal";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { createGroups } from "../../../services/emailGroupServices";
import {
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  Users,
  Plus,
  Trash2,
  X,
  Mail,
  User,
  Upload,
  UserPlus,
  Info,
  Settings,
} from "lucide-react";

export default function EmailCreateGroupModal({
  isOpen,
  close,
  refreshGroups,
}) {
  const nameInputRef = useRef(null);

  const [mode, setMode] = useState("manual");
  const [excelFile, setExcelFile] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [users, setUsers] = useState([{ name: "", email: "" }]);

  const handleDelete = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleAddUser = () => {
    if (!name.trim() || !isValidEmail(email)) {
      toast.error("Valid name and email are required");
      return;
    }
    setUsers([...users, { name, email }]);
    setName("");
    setEmail("");
    nameInputRef.current?.focus();
  };

  const handleCreateGroup = () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (mode === "manual") {
      const validUsers = users.filter(
        (user) => user.name.trim() && isValidEmail(user.email)
      );

      if (validUsers.length === 0) {
        toast.error("Please add at least one valid user");
        return;
      }

      const payload = {
        groupName,
        users: validUsers,
      };

      toast
        .promise(createGroups(payload), {
          loading: "Creating group...",
          success: "Group created successfully",
          error: (err) => err.message || "Failed to create group",
        })
        .then(() => {
          refreshGroups();
          close();
        });

      return;
    }

    if (mode === "excel") {
      if (!excelFile) {
        toast.error("Please select an Excel file");
        return;
      }

      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("groupName", groupName);

      toast
        .promise(createGroups(formData), {
          loading: "Uploading Excel...",
          success: "Group created successfully from Excel",
          error: (err) => err.message || "Failed to create group from Excel",
        })
        .then(() => {
          refreshGroups();
          close();
        });
    }
  };

  const validUsers = users.filter(
    (user) => user.name.trim() && isValidEmail(user.email)
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      ariaHideApp={false}
      contentLabel="Create Email Group Modal"
      overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      className="bg-white/95 backdrop-blur-xl no-scrollbar w-full purple-scroll max-w-[520px] max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-white/20 relative focus:outline-none transform transition-all duration-300"
    >
      <div className="flex items-center justify-between p-2 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#4F22D8] rounded-xl flex items-center justify-center shadow-lg">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Create Email Group
            </h2>
            <p className="text-sm text-gray-500">
              Add members manually or import from Excel
            </p>
          </div>
        </div>

        <button
          onClick={close}
          aria-label="Close modal"
          className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200 group"
        >
          <X className="w-4 h-4 text-gray-600 group-hover:text-gray-800" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Settings className="w-4 h-4 text-indigo-500" />
            Group Name
          </label>
          <input
            type="text"
            value={groupName}
            placeholder="Enter a unique group name"
            onChange={(e) => setGroupName(e.target.value)}
            className="w-full px-4 h-[44px] border border-gray-200 rounded-xl text-sm bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#4F22D8] focus:border-transparent transition-all duration-200"
          />
        </div>

        <div className="bg-gray-50/30 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-6 space-y-6">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <UserPlus className="w-4 h-4 text-[#4F22D8]" />
              Add Members
            </label>

            <div className="flex gap-4">
              {[
                { value: "manual", label: "Manual Entry", icon: User },
                {
                  value: "excel",
                  label: "Excel Import",
                  icon: FileSpreadsheet,
                },
              ].map(({ value, label, icon: Icon }) => (
                <label
                  key={value}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className="relative">
                    <input
                      type="radio"
                      name="mode"
                      value={value}
                      checked={mode === value}
                      onChange={(e) => setMode(e.target.value)}
                      className="!sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                        mode === value
                          ? "border-[#4F22D8] bg-[#4F22D8]"
                          : "border-gray-300 bg-white group-hover:border-indigo-300"
                      }`}
                    >
                      {mode === value && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                      {label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {mode === "manual" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    ref={nameInputRef}
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-[40px] h-[44px] pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-[40px] pr-4 h-[44px] py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              <button
                onClick={handleAddUser}
                disabled={!name.trim() || !isValidEmail(email)}
                className="w-full bg-[#4F22D8] h-[44px] cursor-pointer disabled:from-gray-300 disabled:to-gray-400 text-white py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:shadow-none disabled:bg-gray-300"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </button>

              {validUsers.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#4F22D8]" />
                      Added Members ({validUsers.length})
                    </h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {validUsers.map((user, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                            <span className="text-xs font-semibold text-[#4F22D8]">
                              {index + 1}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.name}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDelete(index)}
                          className="w-8 h-8 cursor-pointer rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors duration-200 group"
                          aria-label={`Remove ${user.name}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500 group-hover:text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === "excel" && (
            <div className="space-y-6">
              <div className="space-y-4">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setExcelFile(file || null);
                  }}
                  className="hidden"
                  id="excel-upload"
                />
                <label
                  htmlFor="excel-upload"
                  className={`flex items-center justify-center gap-3 border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer transition-all duration-200 ${
                    excelFile
                      ? "border-green-300 bg-green-50 text-green-700"
                      : "border-gray-300 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50 text-gray-600"
                  }`}
                >
                  {excelFile ? (
                    <>
                      <CheckCircle className="w-6 h-6" />
                      <div className="text-center">
                        <p className="font-semibold">{excelFile.name}</p>
                        <p className="text-sm opacity-75">
                          File selected successfully
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6" />
                      <div className="text-center">
                        <p className="font-semibold">Upload Excel File</p>
                        <p className="text-sm opacity-75">Click to browse</p>
                        <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mt-1">
                          <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          <span>
                            Allowed file types:{" "}
                            <span className="font-medium">.xlsx, .xls</span>
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </label>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Info className="w-5 h-5 text-[#4F22D8]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl text-gray-900">
                      Excel Format Requirements
                    </h3>
                    <p className="text-sm text-gray-600">
                      Follow this format for successful import
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-900">
                          Name
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-900">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-3 text-gray-800">John Doe</td>
                        <td className="px-4 py-3 text-gray-600">
                          john@example.com
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        Header Format
                      </p>
                      <p className="text-xs text-amber-700">
                        Use exact headers:{" "}
                        <code className="bg-amber-100 px-1 rounded">Name</code>{" "}
                        and{" "}
                        <code className="bg-amber-100 px-1 rounded">Email</code>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        Email Format
                      </p>
                      <p className="text-xs text-green-700">
                        Valid email addresses only (e.g., user@domain.com)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleCreateGroup}
            disabled={
              !groupName.trim() ||
              (mode === "manual" && validUsers.length === 0) ||
              (mode === "excel" && !excelFile)
            }
            className="w-full bg-[#4F22D8] h-[44px] cursor-pointer disabled:from-gray-300 disabled:to-gray-400 disabled:bg-gray-300 text-white py-3 rounded-xl text-base font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:shadow-none"
          >
            <Users className="w-5 h-5" />
            Create Group
          </button>
        </div>
      </div>
    </Modal>
  );
}
